import { useEffect, useRef } from 'react';

const solutions = [
  {
    icon: 'ri-pulse-line',
    num: '01',
    title: 'Operational Intelligence Systems',
    description:
      'End-to-end automation architectures that capture, qualify, and route operational signals in real time — eliminating manual bottlenecks across your entire workflow.',
    tags: ['Signal Capture', 'Process Automation', 'Real-Time Routing'],
    imgPrompt: 'abstract digital data flow visualization showing interconnected nodes and pathways, dark minimal technology art, warm golden light accents, clean premium aesthetic, no text, 800x500',
  },
  {
    icon: 'ri-database-2-line',
    num: '02',
    title: 'Decision Infrastructure Deployment',
    description:
      'Structured data environments built for executives and operators who need precise visibility across pipelines, teams, and outcomes — without noise.',
    tags: ['CRM Intelligence', 'Pipeline Visibility', 'KPI Architecture'],
    imgPrompt: 'modern executive dashboard on large monitor showing clean data visualizations and charts, sleek dark corporate office environment, minimal professional technology setup, no text, 800x500',
  },
  {
    icon: 'ri-bar-chart-box-line',
    num: '03',
    title: 'Revenue Capture Systems',
    description:
      'Automated intake, qualification, and follow-up sequences designed to convert missed opportunities into booked revenue with no manual intervention required.',
    tags: ['Lead Qualification', 'Automated Follow-Up', 'Conversion Tracking'],
    imgPrompt: 'upward trending business growth graph visualization with golden light rays on dark background, premium data analytics concept, clean minimal financial technology art, no text, 800x500',
  },
  {
    icon: 'ri-shield-check-line',
    num: '04',
    title: 'Execution Monitoring Systems',
    description:
      'Compliance-aware oversight layers that monitor operational execution, flag deviation, and enforce documentation standards across regulated industries.',
    tags: ['Compliance Monitoring', 'Execution Tracking', 'Audit Trails'],
    imgPrompt: 'security and compliance monitoring screen showing operational dashboards and status indicators, clean dark technology interface, professional institutional atmosphere, no text, 800x500',
  },
];

export default function SolutionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('[data-animate]');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 110);
            });
          }
        });
      },
      { threshold: 0.06 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solution" className="bg-white dark:bg-[#0B0B0C] py-28 md:py-40 border-t border-[#E8E8ED] dark:border-[#252528]">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={sectionRef}>
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#5BBFB5]" />
            <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              Our Systems
            </span>
            <span className="w-8 h-px bg-[#5BBFB5]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1D1D1F] dark:text-[#F2F0EC] tracking-tight leading-tight mb-6">
            Structured for Precision.
            <br />
            <span className="text-[#C9A840]">Deployed for Impact.</span>
          </h2>
          <p className="text-[#6E6E73] dark:text-[#96969E] text-lg leading-relaxed">
            Each system is designed as operational infrastructure — not software.
            We build the architecture your organization needs to operate at an institutional level.
          </p>
        </div>

        {/* Solutions — alternating large feature rows */}
        <div className="space-y-8">
          {solutions.map((s, i) => (
            <div
              key={s.title}
              data-animate
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.8s ease ${i * 0.1}s`,
              }}
              className={`group flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 border border-[#E8E8ED] dark:border-[#252528] hover:border-[#C9A840]/30 transition-all duration-500 overflow-hidden`}
            >
              {/* Image panel */}
              <div className="w-full lg:w-5/12 h-56 md:h-72 lg:h-auto relative overflow-hidden flex-shrink-0">
                <img
                  src={`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28s.imgPrompt%29%7D&width=800&height=500&seq=sol-${s.num}&orientation=landscape`}
                  alt={s.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-${i % 2 === 0 ? 'r' : 'l'} from-transparent to-white/20 dark:to-[#0B0B0C]/30`} />
                {/* Number badge */}
                <div className="absolute bottom-4 left-4 w-10 h-10 border border-[#C9A840]/60 bg-[#0B0B0C]/60 backdrop-blur-sm flex items-center justify-center">
                  <span className="text-[#C9A840] text-xs font-bold">{s.num}</span>
                </div>
              </div>

              {/* Content panel */}
              <div className="flex-1 p-8 md:p-12 bg-[#FBFBFD] dark:bg-[#111113] flex flex-col justify-center relative">
                {/* Gold top accent on hover */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-[#C9A840] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                <div className="w-10 h-10 flex items-center justify-center border border-[#E8E8ED] dark:border-[#252528] group-hover:border-[#C9A840]/50 mb-7 transition-colors duration-300">
                  <i className={`${s.icon} text-[#C9A840] text-lg`}></i>
                </div>

                <h3 className="text-[#1D1D1F] dark:text-[#F2F0EC] text-2xl font-bold tracking-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-[#6E6E73] dark:text-[#96969E] text-base leading-relaxed mb-8">
                  {s.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {s.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[#A08840] text-xs px-3 py-1.5 border border-[#C9A840]/25 bg-[#C9A840]/5"
                      style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
